import { Fragment, type ReactNode } from 'react';
import { ExternalLink, Phone, Reply } from 'lucide-react';
import type { PreviaWhatsApp } from '@/lib/comunicaciones';

/**
 * Colores fijos y en `style`, no con clases: el modo oscuro del CRM oscurece
 * `.bg-white` en toda la app, y una burbuja de WhatsApp es blanca siempre.
 *
 * Un mensaje de plantilla como lo pinta WhatsApp: burbuja blanca sobre el
 * fondo beis del chat, cabecera (texto o imagen), cuerpo, pie gris y los
 * botones debajo. Sin `dangerouslySetInnerHTML`: el formato de WhatsApp
 * (*negrita*, _cursiva_, ~tachado~) se convierte en nodos de React.
 */
export default function BurbujaWhatsApp({ previa, hora = '10:30' }: { previa: PreviaWhatsApp; hora?: string }) {
  return (
    <div className="rounded-xl p-4" style={{ background: '#EFE7DE' }}>
      <div className="max-w-[320px]">
        <div className="rounded-lg rounded-tl-none shadow-sm overflow-hidden text-[14px] leading-snug" style={{ color: '#111B21', background: '#FFFFFF' }}>
          {previa.imagen && <img src={previa.imagen} alt="" className="w-full max-h-48 object-cover" />}
          <div className="px-2.5 pt-2 pb-1.5 space-y-1">
            {previa.cabecera && <div className="font-semibold">{formato(previa.cabecera)}</div>}
            <div className="whitespace-pre-wrap break-words">{formato(previa.cuerpo)}</div>
            {previa.pie && <div className="text-[12px]" style={{ color: '#667781' }}>{previa.pie}</div>}
            <div className="text-[11px] text-right" style={{ color: '#667781' }}>{hora}</div>
          </div>
        </div>
        {previa.botones.length > 0 && (
          <div className="mt-0.5 space-y-0.5">
            {previa.botones.map((b, i) => (
              <div key={i} className="rounded-lg shadow-sm py-2 text-center text-[14px] font-medium flex items-center justify-center gap-1.5" style={{ color: '#027EB5', background: '#FFFFFF' }} title={b.url}>
                {b.tipo === 'URL' ? <ExternalLink size={14} /> : b.tipo === 'PHONE_NUMBER' ? <Phone size={14} /> : <Reply size={14} />}
                {b.texto}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** *negrita*, _cursiva_ y ~tachado~, como los entiende WhatsApp. */
function formato(texto: string): ReactNode {
  const partes: ReactNode[] = [];
  const re = /\*([^*\n]+)\*|_([^_\n]+)_|~([^~\n]+)~/g;
  let ultimo = 0;
  for (const m of texto.matchAll(re)) {
    if (m.index! > ultimo) partes.push(texto.slice(ultimo, m.index));
    if (m[1]) partes.push(<strong key={m.index}>{m[1]}</strong>);
    else if (m[2]) partes.push(<em key={m.index}>{m[2]}</em>);
    else partes.push(<s key={m.index}>{m[3]}</s>);
    ultimo = m.index! + m[0].length;
  }
  if (ultimo < texto.length) partes.push(texto.slice(ultimo));
  return partes.map((p, i) => <Fragment key={i}>{p}</Fragment>);
}
